import {
  TextField as MUITextField,
  TextFieldProps,
  styled,
} from '@mui/material';
import { forwardRef } from 'react';

import { Text } from '../../Text';
import { RequiredStar } from './ui/RequiredStar';

const noNumberInputArrowsStyle = {
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input': {
    MozAppearance: 'textfield',
  },
};

const TextFieldWrapper = styled('div')<{ disabled?: boolean; error?: boolean }>(
  ({ theme, disabled, error }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),

    '& .TextField__Label': {
      transition: '.15s',
    },

    ...(!error &&
      !disabled && {
        '&:focus-within .TextField__Label': {
          color: theme.palette['line-blue-050'],
        },
      }),

    ...(disabled && {
      '& .TextField__Label': {
        color: theme.palette['text-grey-300'],
      },
    }),

    ...(error &&
      !disabled && {
        '& .TextField__Label': {
          color: theme.palette['text-red-100'],
        },
      }),
  }),
);

const _TextField = (props: TextFieldProps, ref: TextFieldProps['ref']) => {
  const { label, slotProps = {}, required, ...args } = props;

  const disabled = args?.disabled || false;
  const error = args.error || false;
  const fullWidth = args?.fullWidth ?? true;
  const size = args?.size ?? 'medium';

  const slotInput = slotProps.input;
  const resolvedInputProps =
    typeof slotInput === 'function' ? slotInput(props) : slotInput || {};

  const slotInputLabel = slotProps.inputLabel;
  const resolvedInputLabelProps =
    typeof slotInputLabel === 'function'
      ? slotInputLabel(props)
      : slotInputLabel || {};

  return (
    <TextFieldWrapper disabled={disabled} error={error}>
      {label && (
        <Text variant='t2' className='TextField__Label'>
          {label}
        </Text>
      )}

      <MUITextField
        ref={ref}
        {...args}
        data-sentry-mark={label || args?.placeholder}
        fullWidth={fullWidth}
        size={size}
        sx={{
          ...noNumberInputArrowsStyle,
          ...args.sx,
        }}
        slotProps={{
          ...slotProps,
          inputLabel: {
            ...resolvedInputLabelProps,
            required: 'required' in resolvedInputLabelProps,
            sx: {
              ...(resolvedInputLabelProps?.sx || {}),
            },
          },
          input: {
            ...resolvedInputProps,
            endAdornment: (
              <>
                {required && <RequiredStar error={!!error} />}
                {resolvedInputProps?.endAdornment}
              </>
            ),
          },
        }}
      />
    </TextFieldWrapper>
  );
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  _TextField,
);
TextField.displayName = 'TextField';
